import {
  ApprovalTask,
  BillingSchedule,
  OrderAllocatedExpense,
  PaymentSchedule,
  PaymentTask,
  PaymentTaskExpenseAccountItem,
  PaymentTaskFixedAsset,
  PurchaseOrder,
  PurchaseOrderAccounting,
  PurchaseOrderOutline,
  RelatedOrder,
  SalesOrder,
  SalesOrderAccounting,
  SalesOrderOutline,
  Team,
  TeamMember,
  User,
  Workspace,
} from '@/hooks/api/models';
import moment from 'moment';
import { Big } from 'big.js';

export const TAX_RATE = 0.1;

export class TaxLogic {
  static calc = (amount: number) => {
    return new Big(amount).times(TAX_RATE).toNumber();
  };
}

export class ConsumptionTaxRuleLogic {
  static nameToLabel = (name: string) => {
    if (name == 'floor') return '切り捨て';
    if (name == 'round') return '四捨五入';
    if (name == 'ceil') return '切り上げ';
    return name;
  };

  static calc = (amount: number, rule: 'floor' | 'round' | 'ceil') => {
    if (rule == 'floor') return Math.floor(amount + TaxLogic.calc(amount));
    if (rule == 'round') return Math.round(amount + TaxLogic.calc(amount));
    if (rule == 'ceil') return Math.ceil(amount + TaxLogic.calc(amount));
    return amount;
  };
}

export class TeamLogic {
  static isAdmin = (workspace: Workspace, team: Team, user: User) => {
    if (user.admin) return true;

    const team_member = team.team_members.find((tm) => tm.user.id == user.id);
    if (team_member == null) return false;

    const adminRole = team_member.roles.find((r) => r.name == 'admin');
    return adminRole != null;
  };
}

export class TeamMemberLogic {
  /**
   * 発注案件を作成可能かどうかを判定
   */
  static canCreatePurchaseOrder = (team: Team, teamMember?: TeamMember): boolean => {
    if (teamMember == null) return false;

    const tm = team.team_members.find((tm) => tm.id == teamMember.id);
    if (tm == null) return false;

    return TeamMemberLogic.hasAdminRole(tm) || TeamMemberLogic.hasApproverRole(tm) || TeamMemberLogic.hasMemberRole(tm);
  };

  /**
   * 受注案件を作成可能かどうかを判定
   */
  static canCreateSalesOrder = (team: Team, teamMember?: TeamMember): boolean => {
    if (teamMember == null) return false;

    const tm = team.team_members.find((tm) => tm.id == teamMember.id);
    if (tm == null) return false;

    return TeamMemberLogic.hasAdminRole(tm) || TeamMemberLogic.hasApproverRole(tm) || TeamMemberLogic.hasMemberRole(tm);
  };

  /**
   * Admin権限を持つかどうかを判定
   */
  static hasAdminRole = (teamMember: TeamMember) => teamMember.roles.some((r) => r.name == 'admin');

  /**
   * Approver権限を持つかどうかを判定
   */
  static hasApproverRole = (teamMember: TeamMember) => teamMember.roles.some((r) => r.name == 'approver');

  /**
   * FinancialOfficer権限を持つかどうかを判定
   */
  static hasFinancialOfficerRole = (teamMember: TeamMember) =>
    teamMember.roles.some((r) => r.name == 'financial_officer');

  /**
   * Member権限を持つかどうかを判定
   */
  static hasMemberRole = (teamMember: TeamMember) => teamMember.roles.some((r) => r.name == 'member');

  /**
   * Backoffice権限を持つかどうかを判定
   */
  static hasBackofficeRole = (teamMember: TeamMember) => teamMember.roles.some((r) => r.name == 'backoffice');

  /**
   * Backoffice権限のみを持つかどうかを判定
   */
  static hasBackofficeOnlyRole = (teamMember: TeamMember) =>
    teamMember.roles.some((r) => r.name == 'backoffice') && teamMember.roles.length == 1;
}

export class PurchaseOrderLogic {
  static totalAmount = (order: PurchaseOrder | PurchaseOrderOutline) => {
    let _totalAmount = 0;
    if (order.spot_amount) {
      _totalAmount += order.spot_amount;
    }
    if (order.monthly_amount) {
      _totalAmount += ProjectLogic.calcTotalAmount(
        order.monthly_amount,
        order.monthly_billing_start_date!,
        order.monthly_billing_end_date!
      );
    }
    return _totalAmount;
  };

  /**
   * 発注案件を編集可能かどうかを判定
   */
  static canUpdate = (team: Team, purchaseOrder: PurchaseOrder, user: User): boolean => {
    const tm = team.team_members.find((tm) => tm.user.id == user.id);
    if (tm) {
      const hasRole =
        TeamMemberLogic.hasAdminRole(tm) ||
        TeamMemberLogic.hasApproverRole(tm) ||
        TeamMemberLogic.hasMemberRole(tm) ||
        TeamMemberLogic.hasFinancialOfficerRole(tm);
      if (hasRole) return true;

      if (TeamMemberLogic.hasBackofficeOnlyRole(tm)) return false;
    }

    const pom = purchaseOrder.purchase_order_members.find((m) => m.user.id == user.id);
    return pom != null;
  };

  static canUpdateAccounting = (team: Team, purchaseOrder: PurchaseOrder, user: User): boolean => {
    const tm = team.team_members.find((tm) => tm.user.id == user.id);
    if (tm == null) return false;

    return TeamMemberLogic.hasFinancialOfficerRole(tm);
  };
}

export class ProjectLogic {
  static calcTotalAmount = (amount: number, startDate: string, endDate: string) => {
    const s = moment(startDate).startOf('month');
    const e = moment(endDate).startOf('month');
    const monthDiff = e.diff(s, 'months');
    if (monthDiff >= 0) {
      return amount * (monthDiff + 1);
    } else {
      return 0;
    }
  };

  // 契約終了日を変更可能かどうかを判定
  static detectContractEndDate({
    cur,
    dst,
  }: {
    cur: {
      contractEndDate: string;
      spotBillingDate: string;
      monthlyBillingEndDate: string;
      deliveryDate: string;
    };
    dst: {
      contractEndDate: string;
      spotBillingDate: string;
      monthlyBillingEndDate: string;
      deliveryDate: string;
    };
  }) {
    if (cur.contractEndDate.length == 0) {
      const maxDate = [dst.spotBillingDate, dst.monthlyBillingEndDate, dst.deliveryDate].reduce((a, b) =>
        a >= b ? a : b
      );
      return maxDate;
    } else if (
      cur.contractEndDate === cur.spotBillingDate ||
      cur.contractEndDate === cur.monthlyBillingEndDate ||
      cur.contractEndDate === cur.deliveryDate
    ) {
      const maxDate = [dst.spotBillingDate, dst.monthlyBillingEndDate, dst.deliveryDate].reduce((a, b) =>
        a >= b ? a : b
      );
      return maxDate;
    }

    return cur.contractEndDate;
  }
}

export class PaymentScheduleLogic {
  static spotAmount = (d: PaymentSchedule) => {
    return d.spot_detail ? d.spot_detail.amount : 0;
  };

  static spotPaymentTaskPaymentAmount = (d: PaymentSchedule) => {
    return d.spot_detail && d.spot_detail.payment_task_payment_amount ? d.spot_detail.payment_task_payment_amount : 0;
  };

  static monthlyAmount = (d: PaymentSchedule) => {
    return d.monthly_detail ? d.monthly_detail.amount : 0;
  };

  static monthlyPaymentTaskPaymentAmount = (d: PaymentSchedule) => {
    return d.monthly_detail && d.monthly_detail.payment_task_payment_amount
      ? d.monthly_detail.payment_task_payment_amount
      : 0;
  };

  static totalAmount = (d: PaymentSchedule) => {
    return PaymentScheduleLogic.spotAmount(d) + PaymentScheduleLogic.monthlyAmount(d);
  };

  static totalPaymentTaskPaymentAmount = (d: PaymentSchedule) => {
    return (
      PaymentScheduleLogic.spotPaymentTaskPaymentAmount(d) + PaymentScheduleLogic.monthlyPaymentTaskPaymentAmount(d)
    );
  };

  static totalPaymentAmount = (d: PaymentSchedule) => {
    let res = 0;
    if (d.spot_detail) {
      if (d.spot_detail.payment_task_payment_amount != null) {
        res += d.spot_detail.payment_task_payment_amount;
      } else {
        res += d.spot_detail.amount;
      }
    }
    if (d.monthly_detail) {
      if (d.monthly_detail.payment_task_payment_amount != null) {
        res += d.monthly_detail.payment_task_payment_amount;
      } else {
        res += d.monthly_detail.amount;
      }
    }

    return res;
  };

  static calcTotalSpotAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.spotAmount(d);
    }, 0);
  };

  static calcTotalSpotPaymentTaskPaymentAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.spotPaymentTaskPaymentAmount(d);
    }, 0);
  };

  static calcTotalMonthlyAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.monthlyAmount(d);
    }, 0);
  };

  static calcTotalMonthlyPaymentTaskPaymentAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.monthlyPaymentTaskPaymentAmount(d);
    }, 0);
  };

  static calcTotalAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.totalAmount(d);
    }, 0);
  };

  static calcTotalPaymentTaskPaymentAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.totalPaymentTaskPaymentAmount(d);
    }, 0);
  };

  static calcTotalPaymentAmount = (schedules: PaymentSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + PaymentScheduleLogic.totalPaymentAmount(d);
    }, 0);
  };

  static yearMonthOptions = (order: PurchaseOrder, schedules: PaymentSchedule[]) => {
    const yearMonths = schedules.map((d) => d.year_month);
    let s =
      order.payment_context.name == 'spot'
        ? moment().startOf('month')
        : moment(order.monthly_billing_start_date).startOf('month');
    let e =
      order.payment_context.name == 'spot'
        ? moment(order.spot_billing_date).startOf('month')
        : moment(order.monthly_billing_end_date).startOf('month');

    const monthDiff = e.diff(s, 'months');
    const results: string[] = [];
    for (let i = 0; i <= monthDiff; i++) {
      const yearMonth = s.clone().add(i, 'month').format('YYYY/MM');
      results.push(yearMonth);
    }
    return results;
  };
}

export class PurchaseOrderAccountingLogic {
  static spotAmount = (d: PurchaseOrderAccounting) => {
    return d.spot_detail ? d.spot_detail.amount : 0;
  };

  static monthlyAmount = (d: PurchaseOrderAccounting) => {
    return d.monthly_detail ? d.monthly_detail.amount : 0;
  };

  static totalAmount = (d: PurchaseOrderAccounting) => {
    return PurchaseOrderAccountingLogic.spotAmount(d) + PurchaseOrderAccountingLogic.monthlyAmount(d);
  };

  static calcTotalSpotAmount = (accountings: PurchaseOrderAccounting[]) => {
    return accountings.reduce((res, d) => {
      return res + PurchaseOrderAccountingLogic.spotAmount(d);
    }, 0);
  };

  static calcTotalMonthlyAmount = (accountings: PurchaseOrderAccounting[]) => {
    return accountings.reduce((res, d) => {
      return res + PurchaseOrderAccountingLogic.monthlyAmount(d);
    }, 0);
  };

  static calcTotalAmount = (accountings: PurchaseOrderAccounting[]) => {
    return accountings.reduce((res, d) => {
      return res + PurchaseOrderAccountingLogic.totalAmount(d);
    }, 0);
  };
}

export class SalesOrderLogic {
  static totalAmount = (order: SalesOrder | SalesOrderOutline) => {
    let _totalAmount = 0;
    if (order.spot_amount) {
      _totalAmount += order.spot_amount;
    }
    if (order.monthly_amount) {
      _totalAmount += ProjectLogic.calcTotalAmount(
        order.monthly_amount,
        order.monthly_billing_start_date!,
        order.monthly_billing_end_date!
      );
    }
    return _totalAmount;
  };

  /**
   * 受注案件を編集可能かどうかを判定
   */
  static canUpdate = (team: Team, salesOrder: SalesOrder, user: User): boolean => {
    const tm = team.team_members.find((tm) => tm.user.id == user.id);
    if (tm) {
      const hasRole =
        TeamMemberLogic.hasAdminRole(tm) ||
        TeamMemberLogic.hasApproverRole(tm) ||
        TeamMemberLogic.hasMemberRole(tm) ||
        TeamMemberLogic.hasFinancialOfficerRole(tm);
      if (hasRole) return true;

      if (TeamMemberLogic.hasBackofficeOnlyRole(tm)) return false;
    }

    const som = salesOrder.sales_order_members.find((m) => m.user.id == user.id);
    return som != null;
  };

  static canUpdateAccounting = (team: Team, user: User): boolean => {
    const tm = team.team_members.find((tm) => tm.user.id == user.id);
    if (tm == null) return false;

    return TeamMemberLogic.hasFinancialOfficerRole(tm);
  };
}

export class BillingScheduleLogic {
  static spotAmount = (d: BillingSchedule) => {
    return d.spot_detail ? d.spot_detail.amount : 0;
  };

  static spotInvoiceTaskBillingAmount = (d: BillingSchedule) => {
    return d.spot_detail && d.spot_detail.invoice_task_billing_amount ? d.spot_detail.invoice_task_billing_amount : 0;
  };

  static monthlyAmount = (d: BillingSchedule) => {
    return d.monthly_detail ? d.monthly_detail.amount : 0;
  };

  static monthlyInvoiceTaskBillingAmount = (d: BillingSchedule) => {
    return d.monthly_detail && d.monthly_detail.invoice_task_billing_amount
      ? d.monthly_detail.invoice_task_billing_amount
      : 0;
  };

  static totalAmount = (d: BillingSchedule) => {
    return BillingScheduleLogic.spotAmount(d) + BillingScheduleLogic.monthlyAmount(d);
  };

  static totalInvoiceTaskBillingAmount = (d: BillingSchedule) => {
    return (
      BillingScheduleLogic.spotInvoiceTaskBillingAmount(d) + BillingScheduleLogic.monthlyInvoiceTaskBillingAmount(d)
    );
  };

  static totalSalesAmount = (d: BillingSchedule) => {
    let res = 0;
    if (d.spot_detail) {
      if (d.spot_detail.invoice_task_billing_amount != null) {
        res += d.spot_detail.invoice_task_billing_amount;
      } else {
        res += d.spot_detail.amount;
      }
    }
    if (d.monthly_detail) {
      if (d.monthly_detail.invoice_task_billing_amount != null) {
        res += d.monthly_detail.invoice_task_billing_amount;
      } else {
        res += d.monthly_detail.amount;
      }
    }

    return res;
  };

  static calcTotalSpotAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.spotAmount(d);
    }, 0);
  };

  static calcTotalSpotInvoiceTaskBillingAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.spotInvoiceTaskBillingAmount(d);
    }, 0);
  };

  static calcTotalMonthlyAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.monthlyAmount(d);
    }, 0);
  };

  static calcTotalMonthlyInvoiceTaskBillingAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.monthlyInvoiceTaskBillingAmount(d);
    }, 0);
  };

  static calcTotalAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.totalAmount(d);
    }, 0);
  };

  static calcTotalInvoiceTaskBillingAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.totalInvoiceTaskBillingAmount(d);
    }, 0);
  };

  static calcTotalSalesAmount = (schedules: BillingSchedule[]) => {
    return schedules.reduce((res, d) => {
      return res + BillingScheduleLogic.totalSalesAmount(d);
    }, 0);
  };

  static yearMonthOptions = (schedules: BillingSchedule[]) => {
    const yearMonths = schedules.map((d) => d.year_month);

    const results: string[] = [];
    for (let i = 0; i <= yearMonths.length; i++) {
      results.push(yearMonths[i]);
    }
    return results;
  };
}

export class SalesOrderAccountingLogic {
  static spotAmount = (d: SalesOrderAccounting) => {
    return d.spot_detail ? d.spot_detail.amount : 0;
  };

  static monthlyAmount = (d: SalesOrderAccounting) => {
    return d.monthly_detail ? d.monthly_detail.amount : 0;
  };

  static totalAmount = (d: SalesOrderAccounting) => {
    return SalesOrderAccountingLogic.spotAmount(d) + SalesOrderAccountingLogic.monthlyAmount(d);
  };

  static calcTotalSpotAmount = (schedules: SalesOrderAccounting[]) => {
    return schedules.reduce((res, d) => {
      return res + SalesOrderAccountingLogic.spotAmount(d);
    }, 0);
  };

  static calcTotalMonthlyAmount = (schedules: SalesOrderAccounting[]) => {
    return schedules.reduce((res, d) => {
      return res + SalesOrderAccountingLogic.monthlyAmount(d);
    }, 0);
  };

  static calcTotalAmount = (schedules: SalesOrderAccounting[]) => {
    return schedules.reduce((res, d) => {
      return res + SalesOrderAccountingLogic.totalAmount(d);
    }, 0);
  };
}

export class PaymentTaskLogic {
  static categorizedAmount = (d: PaymentTask, assets: PaymentTaskFixedAsset[]) => {
    if (d.payment_amount == null) return 0;

    const totalFixedAmount = assets.reduce((res, d) => res + d.amount, 0);
    return d.non_fixed_asset_amount + totalFixedAmount;
  };

  static unCategorizedAmount = (d: PaymentTask, assets: PaymentTaskFixedAsset[]) => {
    if (d.payment_amount == null) return 0;

    return d.payment_amount - PaymentTaskLogic.categorizedAmount(d, assets);
  };

  static categorizedExpenseAccountItemAmount = (d: PaymentTask, items: PaymentTaskExpenseAccountItem[]) => {
    if (d.payment_amount == null) return 0;

    return items.reduce((res, d) => res + d.amount, 0);
  };

  static unCategorizedExpenseAccountItemAmount = (d: PaymentTask, items: PaymentTaskExpenseAccountItem[]) => {
    if (d.payment_amount == null) return 0;

    return d.payment_amount - PaymentTaskLogic.categorizedExpenseAccountItemAmount(d, items);
  };

  static allocatedAmount = (d: PaymentTask, expenses: OrderAllocatedExpense[]) => {
    if (d.payment_amount == null) return 0;

    const totalFixedAmount = expenses.reduce((res, d) => res + d.amount, 0);
    return d.non_allocable_amount + totalFixedAmount;
  };

  static unAllocatedAmount = (d: PaymentTask, expenses: OrderAllocatedExpense[]) => {
    if (d.payment_amount == null) return 0;

    return d.payment_amount - PaymentTaskLogic.allocatedAmount(d, expenses);
  };
}

export class ApprovalTaskLogic {
  static isApprovalRequest = (task: ApprovalTask) => {
    return ApprovalTaskLogic.currentPhaseName(task) == 'request_approval';
  };

  static isApproved = (task: ApprovalTask) => {
    return ApprovalTaskLogic.currentPhaseName(task) == 'approved';
  };

  static currentPhaseName = (task: ApprovalTask) => {
    if (task.task_name == 'sales_order_contract_task') {
      return task.sales_order_contract_task!.sales_order_contract_task_phase.name;
    }

    if (task.task_name == 'purchase_order_contract_task') {
      return task.purchase_order_contract_task!.purchase_order_contract_task_phase.name;
    }

    if (task.task_name == 'quotation_task') {
      return task.quotation_task!.quotation_task_phase.name;
    }

    return '';
  };

  static isSalesOrderContractTask = (task: ApprovalTask) => task.task_name == 'sales_order_contract_task';

  static isPurchaseOrderContractTask = (task: ApprovalTask) => task.task_name == 'purchase_order_contract_task';

  static isQuotationTask = (task: ApprovalTask) => task.task_name == 'quotation_task';
}

export class SalesOrderOutlineLogic {
  static toLabel = (d: SalesOrderOutline) => {
    let label = `【${d.team_name}】${d.name}：${d.business_partner_display_name}`;

    let dateLabel = SalesOrderOutlineLogic.toDateLabel(d);
    if (dateLabel.length > 0) {
      dateLabel = `（${dateLabel}）`;
    }
    return `${label}${dateLabel}`;
  };

  static toDateLabel = (d: SalesOrderOutline) => {
    let termFrom: string | null = null;
    let termTo: string | null = null;
    // 開始日：契約開始日→請求対象期間開始日　の順であるものを指定。
    if (d.contract_start_date != null) {
      termFrom = moment(d.contract_start_date).format('YYYY/MM/DD');
    } else if (d.monthly_billing_start_date != null) {
      termFrom = moment(d.monthly_billing_start_date).format('YYYY/MM/DD');
    }

    // 終了日：右二つのうち遅い方（契約終了日→請求対象期間終了日）（納品日→請求日）
    let termTo1: string | null = null;
    let termTo2: string | null = null;
    let dateLabel = '';
    if (d.contract_end_date != null) {
      termTo1 = moment(d.contract_end_date).format('YYYY/MM/DD');
    } else if (d.monthly_billing_end_date != null) {
      termTo1 = moment(d.monthly_billing_end_date).format('YYYY/MM/DD');
    }
    if (d.delivery_date != null) {
      termTo2 = moment(d.delivery_date).format('YYYY/MM/DD');
    } else if (d.spot_billing_date != null) {
      termTo2 = moment(d.spot_billing_date).format('YYYY/MM/DD');
    }
    if (termTo1 != null && termTo2 == null) {
      termTo = termTo1;
    } else if (termTo1 == null && termTo2 != null) {
      termTo = termTo2;
    } else if (termTo1 != null && termTo2 != null) {
      termTo = termTo1 > termTo2 ? termTo1 : termTo2;
    }

    if (termFrom != null && termTo == null) {
      dateLabel = `${termFrom}`;
    } else if (termFrom == null && termTo != null) {
      dateLabel = `${termTo}`;
    } else if (termFrom != null && termTo != null) {
      dateLabel = `${termFrom} - ${termTo}`;
    }

    return dateLabel;
  };
}

export class PurchaseOrderOutlineLogic {
  static toLabel = (d: PurchaseOrderOutline) => {
    let label = `【${d.team_name}】${d.name}：${d.business_partner_display_name}`;

    let dateLabel = PurchaseOrderOutlineLogic.toDateLabel(d);
    if (dateLabel.length > 0) {
      dateLabel = `（${dateLabel}）`;
    }
    return `${label}${dateLabel}`;
  };

  static toDateLabel = (d: PurchaseOrderOutline) => {
    let termFrom: string | null = null;
    let termTo: string | null = null;
    // 開始日：契約開始日→請求対象期間開始日　の順であるものを指定。
    if (d.contract_start_date != null) {
      termFrom = moment(d.contract_start_date).format('YYYY/MM/DD');
    } else if (d.monthly_billing_start_date != null) {
      termFrom = moment(d.monthly_billing_start_date).format('YYYY/MM/DD');
    }

    // 終了日：右二つのうち遅い方（契約終了日→請求対象期間終了日）（納品日→請求日）
    let termTo1: string | null = null;
    let termTo2: string | null = null;
    let dateLabel = '';
    if (d.contract_end_date != null) {
      termTo1 = moment(d.contract_end_date).format('YYYY/MM/DD');
    } else if (d.monthly_billing_end_date != null) {
      termTo1 = moment(d.monthly_billing_end_date).format('YYYY/MM/DD');
    }
    if (d.spot_billing_date != null) {
      termTo2 = moment(d.spot_billing_date).format('YYYY/MM/DD');
    }
    if (termTo1 != null && termTo2 == null) {
      termTo = termTo1;
    } else if (termTo1 == null && termTo2 != null) {
      termTo = termTo2;
    } else if (termTo1 != null && termTo2 != null) {
      termTo = termTo1 > termTo2 ? termTo1 : termTo2;
    }

    if (termFrom != null && termTo == null) {
      dateLabel = `${termFrom}`;
    } else if (termFrom == null && termTo != null) {
      dateLabel = `${termTo}`;
    } else if (termFrom != null && termTo != null) {
      dateLabel = `${termFrom} - ${termTo}`;
    }

    return dateLabel;
  };
}
